"use client";

import { Muted } from "@nyntax/design-system/components/typography/muted";
import { Badge } from "@nyntax/design-system/components/ui/badge";
import { Button } from "@nyntax/design-system/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@nyntax/design-system/components/ui/dropdown-menu";
import { createContext, type ReactNode, use, useMemo } from "react";
import { LuFilter } from "react-icons/lu";

// ---------------------------------------------------------------------------
// Group context (each group owns its selection state)
// ---------------------------------------------------------------------------

interface DataTableFilterGroupContextValue {
  selectedSet: Set<string>;
  toggle: (value: string, checked: boolean) => void;
}

const DataTableFilterGroupContext =
  createContext<DataTableFilterGroupContextValue | null>(null);

function useFilterGroupContext(): DataTableFilterGroupContextValue {
  const ctx = use(DataTableFilterGroupContext);
  if (!ctx) {
    throw new Error(
      "DataTableFilterOption must be used within a <DataTableFilterGroup>."
    );
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

interface DataTableFilterProps {
  children: ReactNode;
}

function DataTableFilter({ children }: DataTableFilterProps) {
  return <DropdownMenu>{children}</DropdownMenu>;
}

// ---------------------------------------------------------------------------
// Trigger
// ---------------------------------------------------------------------------

interface DataTableFilterTriggerProps {
  children: ReactNode;
  className?: string;
  /** Total number of active filters across all groups */
  count?: number;
}

function DataTableFilterTrigger({
  children,
  count = 0,
  className,
}: DataTableFilterTriggerProps) {
  return (
    <DropdownMenuTrigger
      render={
        <Button className={className} variant="outline">
          <LuFilter />
          {children}
          {count > 0 ? <Badge variant="secondary">{count}</Badge> : null}
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

interface DataTableFilterContentProps {
  children: ReactNode;
  className?: string;
}

function DataTableFilterContent({
  children,
  className,
}: DataTableFilterContentProps) {
  return (
    <DropdownMenuContent align="start" className={className}>
      {children}
    </DropdownMenuContent>
  );
}

// ---------------------------------------------------------------------------
// Group (submenu per filter dimension)
// ---------------------------------------------------------------------------

interface DataTableFilterGroupProps {
  children: ReactNode;
  /** Submenu trigger label */
  label: string;
  /** Called with the full new selection array when a value is toggled */
  onSelectionChange: (next: string[]) => void;
  /** Currently selected values for this group (controlled) */
  selected: string[];
}

function DataTableFilterGroup({
  children,
  label,
  selected,
  onSelectionChange,
}: DataTableFilterGroupProps) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  function toggle(value: string, checked: boolean) {
    const next = checked
      ? [...selected, value]
      : selected.filter((v) => v !== value);
    onSelectionChange(next);
  }

  return (
    <DataTableFilterGroupContext value={{ selectedSet, toggle }}>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          {label}
          {selected.length > 0 ? (
            <Badge className="ml-auto" variant="secondary">
              {selected.length}
            </Badge>
          ) : null}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent sideOffset={10}>
          {children}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </DataTableFilterGroupContext>
  );
}

// ---------------------------------------------------------------------------
// Option
// ---------------------------------------------------------------------------

interface DataTableFilterOptionProps {
  children: ReactNode;
  className?: string;
  /** Optional count displayed to the right of the label */
  count?: number;
  /** The value this option represents */
  value: string;
}

function DataTableFilterOption({
  children,
  value,
  count,
  className,
}: DataTableFilterOptionProps) {
  const { selectedSet, toggle } = useFilterGroupContext();
  const isChecked = selectedSet.has(value);

  return (
    <DropdownMenuCheckboxItem
      checked={isChecked}
      className={className}
      onCheckedChange={(checked) => toggle(value, checked)}
    >
      {children}
      {count === undefined ? null : (
        <Muted as="span" className="ml-auto tabular-nums">
          {count}
        </Muted>
      )}
    </DropdownMenuCheckboxItem>
  );
}

export type {
  DataTableFilterContentProps,
  DataTableFilterGroupProps,
  DataTableFilterOptionProps,
  DataTableFilterProps,
  DataTableFilterTriggerProps,
};
export {
  DataTableFilter,
  DataTableFilterContent,
  DataTableFilterGroup,
  DataTableFilterOption,
  DataTableFilterTrigger,
};
